import {Observable} from 'rxjs';
import i18next from 'i18next';
import {List} from 'immutable';

import {combineEpics} from 'web-app/util/redux-observable';
import {showDeleteDialogObservable, showDialogObservable, DialogChoice} from 'web-app/react/dialog';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import * as FileHelpers from 'web-app/react/entities/files/helpers';
import {getApolloClient} from 'web-app/api/clients/famly-app';
import * as AccountSelectors from 'web-app/react/session/account/selectors';

import * as Api from './api';
import * as Actions from './actions';
import * as Constants from './constants';

const draftTimeOutOptions = {
    timeout: 5 * 1000,
};

export const createDraftObservable = (
    recipients,
    from,
    body,
    richTextBody,
    files,
    observationData,
    disableFeedback,
    activities,
    famlyLiveMeetingId,
) => {
    const {imageIds, videoIds, fileIds} = FileHelpers.getFileArrays(files);

    const createObservable = observationData
        ? Api.createOrUpdateObservationDraft(
              undefined,
              recipients,
              body,
              observationData.nextStep,
              observationData.statements,
              observationData.areaRefinements,
              imageIds,
              videoIds,
              fileIds,
              observationData.variant,
              observationData.observedAt,
              draftTimeOutOptions,
          )
        : Api.createPost(
              recipients,
              from,
              body,
              richTextBody,
              imageIds,
              videoIds,
              fileIds,
              false,
              disableFeedback,
              {
                  suppressErrors: true,
                  ...draftTimeOutOptions,
              },
              activities,
              famlyLiveMeetingId,
          );

    return createObservable.map(({draft}) => Actions.createSuccess.action(draft.postId, draft));
};

export const updateDraftObservable = (
    draftId,
    recipients,
    from,
    body,
    richTextBody,
    files,
    observationData,
    disableFeedback,
    activities,
    famlyLiveMeetingId,
) => {
    const {imageIds, videoIds, fileIds} = FileHelpers.getFileArrays(files);

    const updateObservable = observationData
        ? Api.createOrUpdateObservationDraft(
              draftId,
              recipients,
              body,
              observationData.nextStep,
              observationData.statements,
              observationData.areaRefinements,
              imageIds,
              videoIds,
              fileIds,
              observationData.variant,
              observationData.observedAt,
              draftTimeOutOptions,
          )
        : Api.updateDraft(
              draftId,
              recipients,
              from,
              body,
              richTextBody,
              imageIds,
              videoIds,
              fileIds,
              false,
              disableFeedback,
              {
                  suppressErrors: false,
                  ...draftTimeOutOptions,
              },
              activities,
              famlyLiveMeetingId,
          );
    return updateObservable.map(({draft}) => Actions.updateSuccess.action(draftId, draft));
};

export const sendDraftObservable = (
    draftId,
    recipients,
    from,
    body,
    richTextBody,
    files,
    observationData,
    disableFeedback,
    activities,
    famlyLiveMeetingId,
    facebookPageIds,
    rssInstitutionSetIds,
    overridePostBlock,
    sendAsSms,
) => {
    const {imageIds, videoIds, fileIds} = FileHelpers.getFileArrays(files);

    const hasMultipleRecipients = recipients && recipients.split(',').length > 1;

    const getSendObservable = () =>
        Api.sendObservation(
            draftId,
            recipients,
            body,
            observationData.nextStep,
            observationData.statements,
            observationData.areaRefinements,
            imageIds,
            videoIds,
            fileIds,
            observationData.variant,
            observationData.status,
            observationData.observedAt,
        )
            .do(() => {
                // Reset graphql cache
                // Quickfix until we get endpoint with summarized observation totals
                if (observationData.status === Constants.ObservationStatusState.SENT) {
                    getApolloClient().cache.reset();
                }
            })
            .map(() => Actions.sendSuccess.action(draftId));

    const getUpdateObservable = () =>
        Api.updateDraft(
            draftId,
            recipients,
            from,
            body,
            richTextBody,
            imageIds,
            videoIds,
            fileIds,
            true,
            disableFeedback,
            {
                suppressErrors: true,
            },
            activities,
            famlyLiveMeetingId,
            facebookPageIds,
            rssInstitutionSetIds,
            overridePostBlock,
            sendAsSms,
        ).map(() => Actions.sendSuccess.action(draftId));

    if (!observationData) {
        return getUpdateObservable();
    }

    if (hasMultipleRecipients && observationData.status === Constants.ObservationStatusState.SENT) {
        return showDialogObservable({
            description: i18next.t('draft.confirmGroupObservationDescription'),
            title: i18next.t('draft.confirmGroupObservationTitle'),
            okText: i18next.t('feed.post'),
            cancelText: i18next.t('cancel'),
        }).mergeMap(selection => {
            if (selection === DialogChoice.OK) {
                return getSendObservable();
            }

            return Observable.of(Actions.sendAborted.action(draftId));
        });
    }

    return getSendObservable();
};

const deleteDraftObservable = (draft, extraData) => {
    const deleteObservation = draft.isObservation
        ? Api.deleteObservationDraft(draft.postId)
        : Api.deleteDraft(draft.postId);
    return deleteObservation.map(() => Actions.removeSuccess.action(draft.postId, extraData));
};

const getAllDrafts = (author, fetchPosts, fetchObservations, observationVariants, childId) =>
    Observable.zip(
        fetchPosts
            ? Api.getDrafts()
            : Observable.of({
                  drafts: List(),
              }),
        fetchObservations
            ? Api.getObservationDrafts(author, observationVariants, childId)
            : Observable.of({
                  observationDrafts: List(),
              }),
    ).map(([{drafts}, {observationDrafts}]) => Actions.fetchAllSuccess.action(drafts.concat(observationDrafts)));

const fetchAllDrafts = (action$, state$) =>
    action$.ofType(Actions.fetchAll.type).mergeMap(({payload}) => {
        const author = AccountSelectors.loginId(state$.value);

        return getAllDrafts(
            author,
            payload.fetchPosts,
            payload.fetchObservations,
            payload.observationVariants,
            payload.childId,
        ).catch(e => Observable.of(Actions.fetchAllFailed.action(e)));
    });

const fetchPostDraft = action$ =>
    action$.ofType(Actions.fetchPostDraft.type).mergeMap(({payload}) => {
        return Api.fetchPost(payload.postId)
            .map(postDraft => Actions.fetchPostDraftSuccess.action(postDraft))
            .catch(e => Observable.of(Actions.fetchPostDraftFailed.action(e)));
    });

const createDraft = action$ =>
    action$
        .ofType(Actions.create.type)
        .mergeMap(({payload}) =>
            createDraftObservable(
                payload.recipients,
                payload.from,
                payload.body,
                payload.richTextBody,
                payload.files,
                payload.observationData,
                payload.disableFeedback,
                payload.activities,
                payload.famlyLiveMeetingId,
            ).catch(e => Observable.of(Actions.createFailed.action(e))),
        );

const sendDraft = action$ =>
    action$.ofType(Actions.send.type).mergeMap(({payload}) => {
        const {
            draftId,
            recipients,
            from,
            body,
            richTextBody,
            files,
            observationData,
            disableFeedback,
            facebookPageIds,
            rssInstitutionSetIds,
        } = payload;
        return sendDraftObservable(
            draftId,
            recipients,
            from,
            body,
            richTextBody,
            files,
            observationData,
            disableFeedback,
            facebookPageIds,
            rssInstitutionSetIds,
        )
            .mergeMap(success => {
                return Observable.merge(
                    Observable.of(success),
                    Observable.of(GlobalEventActions.updateSuccess.action(i18next.t('feed.postSent'))),
                );
            })
            .catch(e => {
                return Observable.merge(
                    Observable.of(GlobalEventActions.updateError.action(i18next.t('feed.notSent'))),
                    Observable.of(Actions.sendFailed.action(e)),
                );
            });
    });

const removeDraft = action$ =>
    action$.ofType(Actions.remove.type).mergeMap(({payload}) => {
        const {draft, extraData} = payload;

        return showDeleteDialogObservable({
            title: i18next.t('draft.confirmDeleteTitle'),
            description: i18next.t('draft.confirmDeleteDescription'),
        }).mergeMap(selection => {
            if (selection === DialogChoice.OK) {
                return deleteDraftObservable(draft, extraData).catch(() =>
                    Observable.of(Actions.removeFailed.action(draft, extraData)),
                );
            }

            return Observable.of(Actions.removeAborted.action(draft, extraData));
        });
    });

export default combineEpics(fetchAllDrafts, fetchPostDraft, sendDraft, removeDraft, createDraft);
